import type { InfoItem } from './ReadOnlyInfoCard'
import page from '@/pages/NewDeploymentPage.module.css'
import { LookupSelect } from './LookupSelect'
import { ReadOnlyInfoCard } from './ReadOnlyInfoCard'

interface ExistingServerSelectProps<T extends { name: string }> {
  id: string
  /** "Existing RDS server" or "Existing database server". */
  label: string
  servers: readonly T[] | undefined
  value: T | null
  onChange: (server: T | null) => void
  isLoading: boolean
  isError: boolean
  errorMessage?: string
  onRetry: () => void
  /** Said, and blocking, when the resource group has none: "No existing RDS server found in <rg> / <region>." */
  emptyMessage: string
  /** The read-only details of the chosen server. */
  details: (server: T) => InfoItem[]
}

/**
 * Every existing server of one kind in the chosen resource group and region; the user picks one. Nothing is chosen for them, even
 * when there is only one. The chosen server's details are shown read-only.
 */
export function ExistingServerSelect<T extends { name: string }>({
  id,
  label,
  servers,
  value,
  onChange,
  isLoading,
  isError,
  errorMessage,
  onRetry,
  emptyMessage,
  details,
}: ExistingServerSelectProps<T>) {
  const list = servers ?? []
  if (!isLoading && !isError && servers && list.length === 0) {
    return (
      <div className={page.lbInfoBanner} role="alert">
        {emptyMessage}
      </div>
    )
  }
  return (
    <>
      <LookupSelect
        id={id}
        label={label}
        required
        value={value?.name ?? ''}
        options={list.map((server) => ({ value: server.name, label: server.name }))}
        onChange={(name) => onChange(list.find((server) => server.name === name) ?? null)}
        isLoading={isLoading}
        isError={isError}
        errorMessage={errorMessage}
        onRetry={onRetry}
        emptyMessage={emptyMessage}
        hint={list.length > 0 ? `${list.length} server${list.length === 1 ? '' : 's'} in this resource group and region.` : undefined}
      />
      {value && <ReadOnlyInfoCard title={label} items={details(value)} />}
    </>
  )
}
